// Pure helpers for the map screen — the bits worth testing in isolation
// from the (heavy, router/Apollo/Google-Maps-bound) map screen component.
// Discrete jobs:
//
//   - pickInitialView: precedence rules for the first render's region
//     (focus pair → deep-link URL params → persisted last view → null =
//     geolocation).
//   - viewToRegion: serialized view {lat,lng,z} → the Region shape the
//     map view consumes (with the 1.5× aspect ratio we've used historically).
//   - regionToView: Region from a pan/zoom callback → the rounded, stable
//     view we write to both the URL and localStorage.

import {
  latitudeDeltaToZoom,
  zoomToLatitudeDelta,
} from "#app/components/map/map-styles";
import type { Region } from "#app/components/map/map-view";
import type { Omittable } from "@salestrak/ui";

// A fully-resolved map view. All three fields are required to construct a
// Region — the persisted/URL forms below use `null`/`undefined` for "not set".
export type MapView = { lat: number; lng: number; z: number };

// Shape persisted to localStorage. Nullable members so an unset slot reads as
// `null` rather than being absent (matches usePersistedState's defaults shape).
export type PersistedMapView = {
  lat: number | null;
  lng: number | null;
  z: number | null;
};

// Zoom the map lands on when focusing a single address (a global-search pick).
// Shared with the map screen's post-mount navigateToLocation so a cold deep
// link and an in-place focus settle at the same zoom.
export const FOCUS_ZOOM = 16;

// The map view's URL query params, read straight off the route's search
// params (absent from the URL as an explicit `undefined`, so the members
// relax). The route writes numbers — the router serializes a number bare and
// a numeric STRING in quotes (`lat=%2243.6%22`) — but a hand-built query
// string (`URLSearchParams`, an old link) still arrives as a string, so both
// are read. focusLat/focusLng are the one-shot "focus this address" pair;
// lat/lng/z are the ordinary camera position.
export type UrlMapViewParams = Omittable<{
  lat?: number | string;
  lng?: number | string;
  z?: number | string;
  focusLat?: number | string;
  focusLng?: number | string;
}>;

// One coordinate param as a number, or NaN when it is absent or unusable.
// Gate the Number() coercion on a non-empty trimmed string: a param present
// but empty (`?lat=`) reaches us as `""` and `Number("")` returns 0, so a
// blank param would otherwise read as the perfectly finite 0 — Null Island.
export function urlNumber(value: number | string | undefined): number {
  if (typeof value === "number") return value;
  return value?.trim() ? Number(value) : NaN;
}

// The one-shot "focus this address" pair, or null when it is absent or
// unusable. The map screen's focus effect shares this so the URL cannot mean
// one thing to the initial view and another to the post-mount pan (it used to
// coerce inline and pan to 0,0 on a blank pair).
export function parseFocusCoords(
  url: UrlMapViewParams,
): { lat: number; lng: number } | null {
  const lat = urlNumber(url.focusLat);
  const lng = urlNumber(url.focusLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// The map route's search params. Numbers stay numbers: the router writes a
// number bare and a numeric string in quotes, so a string here is what put
// `lat=%2243.6%22` in shared links.
export type MapRouteSearch = Omittable<{
  lat?: number;
  lng?: number;
  z?: number;
  /** The project open in the Project panel — kept while it is open, so a
   *  reload, Back or a copied link reopens it. */
  issue?: string;
  /** One-shot seed (the panel's "Show on map"): open a pin for this project
   *  at lat/lng, then clear. */
  issueId?: string;
  addressId?: string;
  focusLat?: number;
  focusLng?: number;
}>;

function searchNumber(value: unknown): number | undefined {
  const n =
    typeof value === "number" || typeof value === "string"
      ? urlNumber(value)
      : NaN;
  return Number.isFinite(n) ? n : undefined;
}

function searchString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

// The route's validateSearch. The router JSON-parses each value, so a bare
// `lat=43.6` arrives as a number and a quoted or hand-built one as a string;
// both read as the number. Anything unusable reads as absent.
export function parseMapRouteSearch(
  search: Record<string, unknown>,
): MapRouteSearch {
  return {
    lat: searchNumber(search.lat),
    lng: searchNumber(search.lng),
    z: searchNumber(search.z),
    issue: searchString(search.issue),
    issueId: searchString(search.issueId),
    addressId: searchString(search.addressId),
    // One-shot focus (e.g. from global-search address results): pan there and
    // find-or-create a pin, popping the infobox.
    focusLat: searchNumber(search.focusLat),
    focusLng: searchNumber(search.focusLng),
  };
}

// Pick the initial view to seed the map with. Precedence:
//   1. ?focusLat&focusLng — an explicit "put me at this address" navigation
//   2. ?lat&lng&z         — an ordinary deep-linked camera position
//   3. the persisted last view
//   4. null — caller leaves initialRegion undefined; the map falls through to
//      its geolocation default
//
// Focus wins because it is a destination the user just chose, whereas lat/lng/z
// is only where the camera happened to be. Without it, arriving at /map from
// global search mounted the map at the persisted last view and relied on a
// post-mount pan to correct it — which is why selecting an address worked when
// already on the map (no mount, pins already loaded) but not from another
// screen. Focus carries no zoom of its own, so it pairs with FOCUS_ZOOM.
export function pickInitialView(
  url: UrlMapViewParams,
  persisted: PersistedMapView,
): MapView | null {
  const focus = parseFocusCoords(url);
  if (focus) return { ...focus, z: FOCUS_ZOOM };
  // A blank param (`?lat=&lng=&z=`) reads as NaN (urlNumber), never as
  // {0,0,0} (Null Island, zoom 0), so it falls through to the persisted view.
  const urlLat = urlNumber(url.lat);
  const urlLng = urlNumber(url.lng);
  const urlZ = urlNumber(url.z);
  if (
    Number.isFinite(urlLat) &&
    Number.isFinite(urlLng) &&
    Number.isFinite(urlZ)
  ) {
    return { lat: urlLat, lng: urlLng, z: urlZ };
  }
  if (persisted.lat !== null && persisted.lng !== null && persisted.z !== null) {
    return { lat: persisted.lat, lng: persisted.lng, z: persisted.z };
  }
  return null;
}

// Build the Region the map view consumes from a discrete view. The ×1.5
// longitudeDelta matches the aspect ratio we've used historically for the
// initial render — the map view itself adjusts the deltas to the actual
// viewport as soon as it lays out.
export function viewToRegion(view: MapView): Region {
  const latitudeDelta = zoomToLatitudeDelta(view.z);
  return {
    latitude: view.lat,
    longitude: view.lng,
    latitudeDelta,
    longitudeDelta: latitudeDelta * 1.5,
    zoom: view.z,
  };
}

// Reduce a Region fired by the map view during pan/zoom to the stable shape
// we serialize to the URL and to localStorage: lat/lng at 5-decimal precision
// (~1m), zoom rounded to int. Prefers r.zoom when the underlying map reports
// it directly; otherwise derives it from latitudeDelta.
export function regionToView(r: Region): MapView {
  const zRaw = r.zoom ?? latitudeDeltaToZoom(r.latitudeDelta);
  return {
    lat: Number(r.latitude.toFixed(5)),
    lng: Number(r.longitude.toFixed(5)),
    z: Math.round(zRaw),
  };
}

/**
 * The query string that focuses the map on one place — the `focusLat`/
 * `focusLng` one-shot, plus the address id when the caller has one so the pin
 * search matches the address rather than the nearest coordinates. Every door
 * that lands a reader on a place (global search, the map's Recent rows, a
 * copied place link) builds its params here so the route reads one grammar.
 */
export function mapFocusSearch(place: {
  lat: number;
  lng: number;
  addressId?: string | null | undefined;
}): string {
  const params = new URLSearchParams({
    focusLat: String(place.lat),
    focusLng: String(place.lng),
  });
  if (place.addressId) params.set("addressId", place.addressId);
  return params.toString();
}
